import { useState, useMemo, useCallback, useRef } from 'react';
import { ArrowLeft, UserPlus, Users, X, UserCircle } from 'lucide-react';
import type { ProjectDetail } from '../../services/projectService';
import type { SprintItem } from '../../services/sprintService';
import type { DailyScrumEntry } from '../../services/dailyScrumService';
import { useSprints } from '../../hooks/useSprints';
import {
  useDailyScrum,
  useUpdateEntry,
  useRemoveEntry,
  useAddEntry,
  useRemoveEntryFromFutureScrums,
  useAddSharedItem,
  useUpdateSharedItem,
  useDeleteSharedItem,
} from '../../hooks/useDailyScrum';
import ScrumMemberRemoveModal from './ScrumMemberRemoveModal';
import { useAllActiveWorkflowStatuses } from '../../hooks/useResolvedWorkflow';
import { buildDynamicColumns, buildCategoryColumns, type BoardColumnDef, type CategoryColumnDef } from '../board/boardUtils';
import { SprintIssueCard } from './ActiveSprintView';
import { usePanelContext } from '../../contexts/PanelContext';
import ScrumEntryAccordion from './ScrumEntryAccordion';
import SharedItemsList from './SharedItemsList';
import Avatar from '../common/Avatar';
import { useProfileModal } from '../common/ProfileModalProvider';
import type { ProfileColorKey } from '@maios/shared';
import Spinner from '../ui/Spinner';

interface Props {
  project: ProjectDetail;
  sprintId: string;
  dailyScrumId: string;
  onBack: () => void;
}

export default function DailyScrumDetailView({ project, sprintId, dailyScrumId, onBack }: Props) {
  const { openProfileModal } = useProfileModal();
  const [selectedMemberIds, setSelectedMemberIds] = useState<Set<string>>(new Set());
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [showAddMember, setShowAddMember] = useState(false);
  const [removingEntry, setRemovingEntry] = useState<DailyScrumEntry | null>(null);
  const [rightPanelWidth, setRightPanelWidth] = useState(480);
  const resizingRef = useRef(false);

  // 2열-3열 경계 드래그 리사이즈
  const handleResizeStart = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    resizingRef.current = true;
    const startX = e.clientX;
    const startWidth = rightPanelWidth;

    const onMove = (ev: MouseEvent) => {
      if (!resizingRef.current) return;
      const delta = startX - ev.clientX;
      setRightPanelWidth(Math.max(320, Math.min(700, startWidth + delta)));
    };
    const onUp = () => {
      resizingRef.current = false;
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [rightPanelWidth]);

  const { data: scrum, isLoading } = useDailyScrum(project.id, sprintId, dailyScrumId);
  const { data: sprints } = useSprints(project.id);
  const activeSprint = sprints?.find((s) => s.status === 'ACTIVE');
  const { openDetailPanel } = usePanelContext();
  // CHG-130 REQ-DHR-017: 회의록 수동 저장 제거 — 23:00 KST 스케줄러가 자동 저장

  // 워크플로우 컬럼
  const { isMultiWorkflow } = useAllActiveWorkflowStatuses(project.id, project.workflow);
  const statusColumns = useMemo(() => buildDynamicColumns(project.workflow.statuses), [project.workflow.statuses]);
  const categoryColumns = useMemo(() => buildCategoryColumns(), []);
  const columns = isMultiWorkflow ? categoryColumns : statusColumns;

  // Mutation hooks
  const updateEntryMutation = useUpdateEntry(project.id, sprintId);
  const removeEntryMutation = useRemoveEntry(project.id, sprintId);
  const addEntryMutation = useAddEntry(project.id, sprintId);
  const removeFromFutureMutation = useRemoveEntryFromFutureScrums(project.id, sprintId);
  const addSharedItemMutation = useAddSharedItem(project.id, sprintId);
  const updateSharedItemMutation = useUpdateSharedItem(project.id, sprintId);
  const deleteSharedItemMutation = useDeleteSharedItem(project.id, sprintId);

  // --- Handlers ---
  const handleUpdateEntry = useCallback(
    (entryId: string, data: { yesterday?: unknown[]; today?: unknown[]; blockers?: unknown[] }) => {
      if (!scrum) return;
      updateEntryMutation.mutate({ dailyScrumId: scrum.dailyScrumId, entryId, data });
    },
    [scrum, updateEntryMutation],
  );

  const handleRemoveEntry = useCallback(
    (entryId: string) => {
      if (!scrum) return;
      const entry = scrum.entries.find((e) => e.entryId === entryId);
      if (entry) {
        const yesterday = (entry.yesterday as unknown[]) ?? [];
        const today = (entry.today as unknown[]) ?? [];
        const blockers = (entry.blockers as unknown[]) ?? [];
        const hasContent = yesterday.length > 0 || today.length > 0 || blockers.length > 0;
        if (hasContent && !window.confirm(`${entry.user.name}의 항목을 삭제하시겠습니까? 입력된 내용이 모두 삭제됩니다.`)) {
          return;
        }
      }
      removeEntryMutation.mutate({ dailyScrumId: scrum.dailyScrumId, entryId });
    },
    [scrum, removeEntryMutation],
  );

  const handleMemberRemove = useCallback(
    (type: 'today' | 'future') => {
      if (!scrum || !removingEntry) return;
      const scrumDate = scrum.date.split('T')[0];
      if (type === 'today') {
        removeEntryMutation.mutate(
          { dailyScrumId: scrum.dailyScrumId, entryId: removingEntry.entryId },
          { onSuccess: () => setRemovingEntry(null) },
        );
      } else {
        removeFromFutureMutation.mutate(
          { userId: removingEntry.userId, fromDate: scrumDate },
          { onSuccess: () => setRemovingEntry(null) },
        );
      }
    },
    [scrum, removingEntry, removeEntryMutation, removeFromFutureMutation],
  );

  const handleAddEntry = useCallback(
    (userId: string) => {
      if (!scrum) return;
      addEntryMutation.mutate(
        { dailyScrumId: scrum.dailyScrumId, userId },
        { onSuccess: () => setShowAddMember(false) },
      );
    },
    [scrum, addEntryMutation],
  );

  const handleAddSharedItem = useCallback(
    (type: string, content: string) => {
      if (!scrum) return;
      addSharedItemMutation.mutate({ dailyScrumId: scrum.dailyScrumId, data: { type, content } });
    },
    [scrum, addSharedItemMutation],
  );

  const handleUpdateSharedItem = useCallback(
    (itemId: string, data: { content?: string; status?: string }) => {
      if (!scrum) return;
      updateSharedItemMutation.mutate({ dailyScrumId: scrum.dailyScrumId, itemId, data });
    },
    [scrum, updateSharedItemMutation],
  );

  const handleDeleteSharedItem = useCallback(
    (itemId: string) => {
      if (!scrum) return;
      deleteSharedItemMutation.mutate({ dailyScrumId: scrum.dailyScrumId, itemId });
    },
    [scrum, deleteSharedItemMutation],
  );

  // 멤버 토글
  const toggleMember = (userId: string) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const selectAll = () => setSelectedMemberIds(new Set());

  // 미참여 멤버
  const nonParticipants = useMemo(() => {
    if (!scrum) return project.members;
    const entryUserIds = new Set(scrum.entries.map((e) => e.userId));
    return project.members.filter((m) => !entryUserIds.has(m.userId));
  }, [scrum, project.members]);

  // 공유사항 / 액션아이템
  const notices = useMemo(() => scrum?.sharedItems.filter((i) => i.type === 'NOTICE') ?? [], [scrum]);
  const actionItems = useMemo(() => scrum?.sharedItems.filter((i) => i.type === 'ACTION_ITEM') ?? [], [scrum]);

  // 멤버별 진행률 계산
  const memberProgress = useMemo(() => {
    const map = new Map<string, { done: number; total: number }>();
    if (!activeSprint) return map;
    for (const item of activeSprint.items) {
      const assigneeId = item.issue.assignee?.id;
      if (!assigneeId) continue;
      const prev = map.get(assigneeId) ?? { done: 0, total: 0 };
      prev.total++;
      if (item.issue.workflowStatus.category === 'DONE') prev.done++;
      map.set(assigneeId, prev);
    }
    return map;
  }, [activeSprint]);

  // 필터된 스프린트 아이템
  const filteredItems = useMemo(() => {
    if (!activeSprint) return [];
    let items = activeSprint.items;
    if (selectedMemberIds.size > 0) {
      const hasUnassigned = selectedMemberIds.has('__unassigned__');
      items = items.filter((i) => {
        if (!i.issue.assignee) return hasUnassigned;
        return selectedMemberIds.has(i.issue.assignee.id);
      });
    }
    return items;
  }, [activeSprint, selectedMemberIds]);

  // 필터된 엔트리
  const filteredEntries = useMemo(() => {
    if (!scrum) return [];
    if (selectedMemberIds.size === 0) return scrum.entries;
    return scrum.entries.filter((e) => selectedMemberIds.has(e.userId));
  }, [scrum, selectedMemberIds]);

  // 컬럼 ID 추출 헬퍼
  const getColId = useCallback((col: BoardColumnDef | CategoryColumnDef) => {
    return 'category' in col ? col.category : col.id;
  }, []);

  const getItemColId = useCallback((item: SprintItem) => {
    return isMultiWorkflow ? item.issue.workflowStatus.category : item.issue.statusId;
  }, [isMultiWorkflow]);

  // 상태별 그룹
  const statusGroups = useMemo(() => {
    return columns.map((col) => {
      const colId = getColId(col);
      const colItems = filteredItems.filter((item) => getItemColId(item) === colId);
      return { col, colId, items: colItems };
    });
  }, [columns, filteredItems, getColId, getItemColId]);

  const formattedDate = scrum
    ? new Date(scrum.date).toLocaleDateString('ko-KR', { year: 'numeric', month: 'long', day: 'numeric' })
    : '';

  if (isLoading) return <Spinner />;

  if (!scrum) {
    return (
      <div className="h-full flex flex-col items-center justify-center">
        <p className="text-sm text-gray-400">스크럼을 찾을 수 없습니다.</p>
        <button onClick={onBack} className="mt-2 text-sm text-primary-600 hover:underline">뒤로 가기</button>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* ─── 최상단: 헤더 + 공유사항 ─── */}
      <div className="shrink-0 space-y-3 pb-3 border-b border-gray-200">
        {/* 헤더 */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 transition-colors">
              <ArrowLeft className="size-4" />
              뒤로
            </button>
            <div>
              <h2 className="text-base font-semibold text-gray-900">{formattedDate} 데일리 스크럼</h2>
              <p className="text-sm text-gray-400">작성자: {scrum.createdBy.name}</p>
            </div>
          </div>
          {/* CHG-130 REQ-DHR-017: 수동 저장 버튼 제거 — 23:00 KST에 자동 저장됨 */}
        </div>

        {/* 공유사항 / 액션아이템 */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">공유사항</h3>
            <SharedItemsList items={notices} type="NOTICE" onAdd={handleAddSharedItem} onUpdate={handleUpdateSharedItem} onDelete={handleDeleteSharedItem} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">액션 아이템</h3>
            <SharedItemsList items={actionItems} type="ACTION_ITEM" onAdd={handleAddSharedItem} onUpdate={handleUpdateSharedItem} onDelete={handleDeleteSharedItem} />
          </div>
        </div>
      </div>

      {/* ─── 본문: 3컬럼 ─── */}
      <div className="flex-1 min-h-0 flex">
        {/* 1열: 멤버 리스트 */}
        <div className="w-[200px] shrink-0 border-r border-gray-200 overflow-y-auto py-3 px-2">
          {/* 헤더 + 멤버 추가 버튼 */}
          <div className="flex items-center justify-between px-2 mb-2">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">멤버</h3>
            <button
              onClick={() => setShowAddMember(!showAddMember)}
              className="flex items-center gap-0.5 text-[11px] text-blue-600 hover:text-blue-800"
            >
              <UserPlus className="size-3" />
              멤버
            </button>
          </div>

          {/* 멤버 추가 드롭다운 */}
          {showAddMember && (
            <div className="border border-gray-200 rounded-lg p-1.5 space-y-0.5 bg-gray-50 mb-2 mx-1">
              {nonParticipants.length > 0 ? nonParticipants.map((m) => (
                <button
                  key={m.userId}
                  onClick={() => handleAddEntry(m.userId)}
                  disabled={addEntryMutation.isPending}
                  className="w-full text-left flex items-center gap-2 px-2 py-1 text-sm rounded hover:bg-white transition-colors"
                >
                  <Avatar
                    size="xs"
                    userId={m.user.id}
                    name={m.user.name}
                    avatarUrl={m.user.avatarUrl}
                    displayColor={m.user.displayColor as ProfileColorKey | null | undefined}
                    firstName={m.user.firstName}
                  />
                  <span className="text-gray-600">{m.user.name}</span>
                </button>
              )) : (
                <p className="text-[11px] text-gray-400 px-2 py-1">모든 멤버가 스크럼에 추가되었습니다.</p>
              )}
            </div>
          )}

          {/* 전체 버튼 */}
          <button
            onClick={selectAll}
            className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors mb-1 ${
              selectedMemberIds.size === 0 ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            <Users className="size-4" />
            전체
          </button>

          {/* 참여 멤버 */}
          <div className="space-y-0.5">
            {scrum.entries.map((entry) => {
              const isSelected = selectedMemberIds.has(entry.userId);
              const progress = memberProgress.get(entry.userId);
              return (
                <div
                  key={entry.userId}
                  className={`group flex items-center gap-1 px-2 py-1.5 rounded-md text-sm transition-colors ${
                    isSelected ? 'bg-blue-50 border-l-2 border-blue-500 text-blue-700' : 'text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  <button
                    onClick={() => toggleMember(entry.userId)}
                    className="flex items-center gap-2 flex-1 min-w-0"
                  >
                    <span onClick={(e) => e.stopPropagation()}>
                      <Avatar
                        size="xs"
                        userId={entry.user.id}
                        name={entry.user.name}
                        avatarUrl={entry.user.avatarUrl}
                        displayColor={entry.user.displayColor as ProfileColorKey | null | undefined}
                        firstName={entry.user.firstName}
                        onClick={() => openProfileModal(entry.user.id)}
                      />
                    </span>
                    <span className="truncate flex-1 text-left">{entry.user.name}</span>
                    {progress && (
                      <span className="text-[11px] text-gray-400 shrink-0">{progress.done}/{progress.total}</span>
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); setRemovingEntry(entry); }}
                    className="opacity-0 group-hover:opacity-100 p-0.5 text-gray-400 hover:text-red-500 transition-all shrink-0"
                    title="멤버 제거"
                  >
                    <X className="size-3" />
                  </button>
                </div>
              );
            })}
          </div>

          {/* 담당자 없음 */}
          {(() => {
            const unassignedCount = activeSprint?.items.filter((i) => !i.issue.assignee).length ?? 0;
            if (unassignedCount === 0) return null;
            const isSelected = selectedMemberIds.has('__unassigned__');
            return (
              <div className="mt-2 pt-2 border-t border-gray-100">
                <button
                  onClick={() => toggleMember('__unassigned__')}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
                    isSelected ? 'bg-blue-50 border-l-2 border-blue-500 text-blue-700' : 'text-gray-500 hover:bg-gray-50'
                  }`}
                >
                  <UserCircle className="size-5 text-gray-400 shrink-0" />
                  <span className="truncate flex-1 text-left">담당자 없음</span>
                  <span className="text-[11px] text-gray-400 shrink-0">{unassignedCount}</span>
                </button>
              </div>
            );
          })()}
        </div>

        {/* 2열: 스프린트 작업 리스트 */}
        <div className="flex-1 min-w-0 border-r border-gray-200 overflow-y-auto py-3 px-3">
          {/* 상태 필터 탭 */}
          <div className="flex items-center gap-1 mb-3 flex-wrap">
            <button
              onClick={() => setStatusFilter(null)}
              className={`px-2.5 py-1 text-sm rounded-full transition-colors ${
                statusFilter === null ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              전체
            </button>
            {columns.map((col) => {
              const colId = getColId(col);
              return (
                <button
                  key={colId}
                  onClick={() => setStatusFilter(colId)}
                  className={`px-2.5 py-1 text-sm rounded-full transition-colors flex items-center gap-1 ${
                    statusFilter === colId ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  <span className="size-2 rounded-full shrink-0" style={{ backgroundColor: col.color }} />
                  {col.title}
                </button>
              );
            })}
          </div>

          {/* 상태별 그룹 */}
          <div className="space-y-4">
            {statusGroups
              .filter((g) => statusFilter === null || g.colId === statusFilter)
              .map((g) => {
                return (
                  <div key={g.colId}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="size-2.5 rounded-full shrink-0" style={{ backgroundColor: g.col.color }} />
                      <span className="text-sm font-semibold text-gray-700">{g.col.title}</span>
                      <span className="text-[11px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">{g.items.length}</span>
                    </div>
                    {g.items.length > 0 ? (
                      <div className="space-y-1.5 pl-4">
                        {g.items.map((item: SprintItem) => (
                          <SprintIssueCard
                            key={item.sprintItemId}
                            item={item}
                            onClick={() => openDetailPanel('issue', item.issue.id)}
                            showStatusBadge={isMultiWorkflow}
                          />
                        ))}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-300 pl-4">이슈 없음</p>
                    )}
                  </div>
                );
              })}
          </div>
        </div>

        {/* 리사이즈 핸들 */}
        <div
          onMouseDown={handleResizeStart}
          className="w-1 shrink-0 cursor-col-resize hover:bg-blue-300 active:bg-blue-400 transition-colors bg-gray-200"
        />

        {/* 3열: 팀원별 항목 */}
        <div className="shrink-0 overflow-y-auto py-3 px-3" style={{ width: rightPanelWidth }}>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">팀원별 항목</h3>
          <div className="space-y-2">
            {filteredEntries.map((entry) => (
              <ScrumEntryAccordion
                key={entry.entryId}
                entry={entry}
                onUpdate={(data) => handleUpdateEntry(entry.entryId, data)}
                onRemove={() => handleRemoveEntry(entry.entryId)}
              />
            ))}
            {filteredEntries.length === 0 && (
              <p className="text-sm text-gray-400 py-4 text-center">선택된 멤버의 항목이 없습니다</p>
            )}
          </div>
        </div>
      </div>

      {/* 멤버 제거 모달 */}
      {removingEntry && (
        <ScrumMemberRemoveModal
          memberName={removingEntry.user.name}
          onConfirm={handleMemberRemove}
          onClose={() => setRemovingEntry(null)}
          isPending={removeEntryMutation.isPending || removeFromFutureMutation.isPending}
        />
      )}
    </div>
  );
}
